/**
 * Sync status, published to the UI.
 *
 * Offline software earns trust by being legible. The user must be able to answer
 * "is my work safe?" at a glance, so this state is surfaced permanently in the
 * top bar rather than hidden behind a settings screen.
 */
export type SyncPhase = 'idle' | 'bootstrapping' | 'pulling' | 'pushing' | 'error';

export interface SyncState {
  online: boolean;
  phase: SyncPhase;
  /** Mutations waiting to reach the server. Zero means everything is durable upstream. */
  pending: number;
  /** Mutations the server refused. They need a human decision. */
  failed: number;
  /** Unresolved edit collisions. */
  conflicts: number;
  lastPullAt: string | null;
  lastError: string | null;
  /** False until the first full replication completes - the UI shows a loader. */
  bootstrapped: boolean;
}

let state: SyncState = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  phase: 'idle',
  pending: 0,
  failed: 0,
  conflicts: 0,
  lastPullAt: null,
  lastError: null,
  bootstrapped: false,
};

const listeners = new Set<() => void>();

export const syncStatus = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  get(): SyncState {
    return state;
  },
};

export function patchStatus(patch: Partial<SyncState>): void {
  // Reference equality drives useSyncExternalStore; skip no-op updates so a
  // 30-second poll that changes nothing does not re-render the whole shell.
  let changed = false;
  for (const key of Object.keys(patch) as Array<keyof SyncState>) {
    if (patch[key] !== undefined && state[key] !== patch[key]) {
      changed = true;
      break;
    }
  }
  if (!changed) return;
  state = { ...state, ...patch };
  for (const l of listeners) l();
}
